import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import useAddSequenceTag from "src/components/Sequence/useAddSequenceTag";
import TooltipWrapper from "../TooltipWrapper";
import useGetTestSequenceTags from "src/components/TestPackage/useGetTestSequenceTags";
import LoadingWrapper from "src/components/Common/Table/Loading";

const ModalAddTagToSequence = ({ sequenceData = [], isOpen, toggle, handleAddSequenceTagSuccess, setIsLoadingSequence }) => {
  const { t } = useTranslation("sequence", "common");
  const [isSubmit, setIsSubmit] = useState(false);

  const handleCancel = () => {
    toggle();
    document.body.click();
  };

  const { submitData, isLoading } = useAddSequenceTag(handleCancel, handleAddSequenceTagSuccess);
  const { data: tags, isFetching: isFetchingTags } = useGetTestSequenceTags(sequenceData?.test_package_id, {
    enabled: !!sequenceData?.test_package_id,
    refetchOnWindowFocus: false,
  });

  const handleSubmit = (data) => {
    setIsSubmit(true);
    setIsLoadingSequence(true);
    const sequenceTagData = {
      id: sequenceData?.id,
      test_sequence_tag_ids: [data],
    };
    return submitData({ data: sequenceTagData });
  };

  return (
    <Modal centered isOpen={isOpen} toggle={toggle} size="lg" backdrop="static" className="add-sequence-tag-modal">
      <ModalHeader>
        <div className="d-flex align-items-center">
          <span className="d-inline-block m-r-6 text-nowrap">{t("add_tag_modal.sequence_title")}</span>
          <TooltipWrapper
            id={`tooltip-tag-sequence-modal-title-${sequenceData?.id}`}
            tooltipText={sequenceData?.combination_id || ""}
            checkOverflow={true}
            tooltipClassName="max-width-by-px-300"
          >
            <span className="d-inline-block overflow-hidden text-truncate important-max-width-by-px-200">
              {sequenceData?.combination_id}
            </span>
          </TooltipWrapper>
          <span className="d-inline-block m-x-6">-</span>
          <span className="d-inline-block text-nowrap">{t("add_tag_modal.add_tag_title")}</span>
        </div>
      </ModalHeader>
      <ModalBody className="p-b-0">
        {isFetchingTags ? (
          <div className="d-flex justify-content-center align-items-center min-height-by-px-200">
            <Spinner size="sm" />
          </div>
        ) : (
          <>
            {!isEmpty(tags?.data?.items) ? (
              <LoadingWrapper loading={isLoading}>
                <div className="tag-list-container px-2 max-height-by-px-416 overflow-auto">
                  <div className="list-group">
                    {orderBy(tags?.data?.items, ["display_order"], ["asc"]).map((tag, index) => (
                      <div
                        key={tag.id}
                        className={`list-group-item d-flex align-items-center py-1 px-2 border-0 rounded-pill ${
                          index !== tags?.data?.items.length - 1 ? "mb-2" : ""
                        }`}
                        onClick={() => !isSubmit && handleSubmit(tag?.id)}
                        style={{ backgroundColor: tag.color }}
                      >
                        <TooltipWrapper
                          id={`tooltip-tag-sequence-modal-title-${tag?.display_order}`}
                          tooltipText={tag.name || ""}
                          checkOverflow={true}
                          tooltipClassName="max-width-by-px-300"
                        >
                          <div className="tag-name text-truncate overflow-hidden">{`#${tag.display_order}: ${tag.name}`}</div>
                        </TooltipWrapper>
                      </div>
                    ))}
                  </div>
                </div>
              </LoadingWrapper>
            ) : (
              <div className="text-center py-2">
                <h5 className="text-muted">{t("add_tag_modal.no_tags_available")}</h5>
                <p className="text-muted">{t("add_tag_modal.no_tags_description")}</p>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-center p-a-16">
        <div className="d-flex justify-content-center p-a-16">
          <button type="button" className="btn btn-sm btn-secondary m-x-10 width-by-px-160" disabled={isSubmit} onClick={handleCancel}>
            {t("cancel")}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ModalAddTagToSequence;
